import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Field, Sheet } from '../components/ui';
import { Cover } from '../components/Cover';
import { useBooks, useStore } from '../hooks';
import { CATEGORIES, DESTINATIONS, DESTINATION_LABELS, type Copy, type Work, type Destination } from '../../shared/types';
import { addBook, updateCopy, updateWork, deleteCopies } from '../lib/actions';
import { findExistingWork, shelvesOf } from '../lib/model';
import { toast } from '../components/toast';
import { IsbnScanner } from '../components/Scanner';
import { CropSheet } from './CoverPicker';
import { apiJson } from '../lib/http';
import { autoCover, searchCandidates, storeFromUrl, clientLookupIsbn, type Candidate } from '../lib/covers';
import { blobToBitmap, fullCrop, uploadCover, type Crop } from '../lib/images';

const CONDITIONS = ['Nouă', 'Foarte bună', 'Bună', 'Uzată', 'Deteriorată'];
const AGE_GROUPS = ['0–3 ani', '3–6 ani', '6–9 ani', '9–12 ani', '12+ ani'];

function useShelves() {
  const books = useBooks();
  return useMemo(() => shelvesOf(books).map(s => s.name), [books]);
}

function CopyFields({ c, set, shelves }: { c: Partial<Copy>; set: (k: keyof Copy, v: any) => void; shelves: string[] }) {
  return (
    <div class="space-y-4">
      <Field label="Raft / locație">
        <input class="field" list="shelf-options" value={c.shelf || ''} onInput={e => set('shelf', (e.target as HTMLInputElement).value)} placeholder="ex. Raft Living" />
        <datalist id="shelf-options">{shelves.map(s => <option key={s} value={s} />)}</datalist>
      </Field>
      <div>
        <span class="label">Destinație</span>
        <div class="flex flex-wrap gap-2">
          {DESTINATIONS.map(d => <button type="button" key={d} class={c.destination === d ? 'chip-on' : 'chip-off'} onClick={() => set('destination', d)}>{DESTINATION_LABELS[d]}</button>)}
        </div>
      </div>
      {c.destination === 'copii' && (
        <Field label="Grupă de vârstă">
          <select class="field" value={c.age_group || ''} onChange={e => set('age_group', (e.target as HTMLSelectElement).value)}>
            <option value="">—</option>{AGE_GROUPS.map(a => <option key={a}>{a}</option>)}
          </select>
        </Field>
      )}
      {c.destination === 'biblioteca_publica' && (
        <Field label="Termen de returnare la bibliotecă">
          <input type="date" class="field" value={c.due_date || ''} onInput={e => set('due_date', (e.target as HTMLInputElement).value)} />
        </Field>
      )}
      <div class="grid grid-cols-2 gap-3">
        <Field label="Stare">
          <select class="field" value={c.condition || 'Bună'} onChange={e => set('condition', (e.target as HTMLSelectElement).value)}>
            {CONDITIONS.map(x => <option key={x}>{x}</option>)}
          </select>
        </Field>
        <Field label="Cutie (pod)">
          <input class="field" value={c.box || ''} onInput={e => set('box', (e.target as HTMLInputElement).value)} placeholder="ex. Cutia 4" />
        </Field>
      </div>
    </div>
  );
}

function WorkFields({ w, set }: { w: Partial<Work>; set: (k: keyof Work, v: any) => void }) {
  return (
    <div class="space-y-4">
      <Field label="Titlu *"><input class="field" value={w.title || ''} onInput={e => set('title', (e.target as HTMLInputElement).value)} required /></Field>
      <Field label="Autor(i)"><input class="field" value={w.authors || ''} onInput={e => set('authors', (e.target as HTMLInputElement).value)} placeholder="ex. Steve Peters" /></Field>
      <div class="grid grid-cols-2 gap-3">
        <Field label="ISBN"><input class="field" inputMode="numeric" value={w.isbn || ''} onInput={e => set('isbn', (e.target as HTMLInputElement).value)} /></Field>
        <Field label="Categorie">
          <select class="field" value={w.category || 'Diverse'} onChange={e => set('category', (e.target as HTMLSelectElement).value)}>
            {[...(w.category && !(CATEGORIES as readonly string[]).includes(w.category) ? [w.category] : []), ...CATEGORIES].map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Editură"><input class="field" value={w.publisher || ''} onInput={e => set('publisher', (e.target as HTMLInputElement).value)} /></Field>
        <Field label="An"><input class="field" inputMode="numeric" value={w.year ?? ''} onInput={e => set('year', Number((e.target as HTMLInputElement).value) || null)} /></Field>
      </div>
      <Field label="Link copertă (opțional)"><input class="field" value={w.cover_url || ''} onInput={e => set('cover_url', (e.target as HTMLInputElement).value || null)} placeholder="https://…" /></Field>
      <Field label="Sinopsis"><textarea class="field min-h-[120px]" value={w.synopsis || ''} onInput={e => set('synopsis', (e.target as HTMLTextAreaElement).value)} /></Field>
      <Field label="Etichete"><input class="field" value={w.tags || ''} onInput={e => set('tags', (e.target as HTMLInputElement).value)} placeholder="#clasic #cadou" /></Field>
    </div>
  );
}

export function AddBookSheet({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (workId: string) => void }) {
  const s = useStore();
  const shelves = useShelves();
  const [w, setW] = useState<Partial<Work>>({ category: 'Diverse' });
  const [c, setC] = useState<Partial<Copy>>({ destination: 'imprumut', condition: 'Bună' });
  const [scanning, setScanning] = useState(false);
  const [looking, setLooking] = useState(false);
  const [cands, setCands] = useState<Candidate[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);        // URL candidat | 'photo' | null
  const [chosenByUser, setChosenByUser] = useState(false);
  const [photo, setPhoto] = useState<{ bmp: ImageBitmap; crop?: Crop } | null>(null);
  const [cropping, setCropping] = useState<ImageBitmap | null>(null);
  const [stack, setStack] = useState(() => localStorage.getItem('cc10_stack') === '1');
  const [count, setCount] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const existing = w.title && w.title.trim().length > 2 ? findExistingWork(s.view, w.title, w.authors || '', w.isbn || '') : null;

  const reset = () => { setW({ category: 'Diverse' }); setCands([]); setChosen(null); setChosenByUser(false); setPhoto(null); };
  const close = () => { reset(); setCount(0); onClose(); };

  const onIsbn = async (isbn13: string) => {
    setScanning(false); setLooking(true);
    setW(prev => ({ ...prev, isbn: isbn13 }));
    try {
      let { book } = await apiJson<{ book: any }>(`/api/lookup?isbn=${isbn13}`);
      if (!book?.title) { const local = await clientLookupIsbn(isbn13); if (local) book = { ...book, ...local }; }
      if (book?.title) {
        setW(prev => ({ ...prev, isbn: isbn13, title: book.title, authors: book.authors || '', publisher: book.publisher || prev.publisher, year: book.year || prev.year, synopsis: book.synopsis || prev.synopsis }));
        toast(`Găsită: „${book.title}”`);
      } else {
        toast('ISBN-ul nu apare online. Completează titlul (sau fă poză copertei).', 'error');
      }
      const list = await searchCandidates({ title: book?.title || '', authors: book?.authors || '', isbn: isbn13 });
      setCands(list); if (list[0]) { setChosen(list[0].url); setChosenByUser(false); }
    } catch (e) { toast((e as Error).message, 'error'); }
    setLooking(false);
  };

  const findCovers = async () => {
    if (!w.title?.trim()) { toast('Scrie întâi titlul', 'error'); return; }
    setLooking(true);
    try { const list = await searchCandidates({ title: w.title, authors: w.authors || '', isbn: w.isbn || '' }); setCands(list); if (list[0] && !chosen) setChosen(list[0].url); }
    catch (e) { toast((e as Error).message, 'error'); }
    setLooking(false);
  };

  const save = async (asCopyOfExisting: boolean) => {
    if (!w.title?.trim()) { toast('Titlul este obligatoriu', 'error'); return; }
    const reuse = asCopyOfExisting && existing ? existing : null;
    const { workId } = await addBook(w, c, reuse?.id);
    toast(reuse ? 'Exemplar adăugat la cartea existentă' : `„${w.title}” a fost adăugată 📚`);
    // coperta se procesează în fundal, ca să poți trece la următoarea carte imediat
    if (!reuse || !reuse.cover_url) {
      const ph = photo, ch = chosen, user = chosenByUser, work = { ...w, id: workId } as Work;
      void (async () => {
        try {
          if (ch === 'photo' && ph) {
            const url = await uploadCover(ph.bmp, ph.crop || fullCrop(ph.bmp), { workId, source: 'photo' });
            await updateWork(workId, { cover_url: url, cover_source: 'photo' });
          } else if (ch) {
            await storeFromUrl(workId, ch, user ? 'verified' : 'auto');
          } else if (!user) {
            await autoCover(work); // titlu scris de mână → căutăm coperta automat (marcată „de verificat”)
          }
        } catch { /* fără copertă → rămâne coperta 3D; o poți schimba din fișă */ }
      })();
    }
    setCount(n => n + 1);
    reset();
    if (stack) { setScanning(true); } else { setCount(0); onClose(); onAdded(workId); }
  };

  if (!open) return null;
  if (cropping) return <CropSheet bmp={cropping} onCancel={() => setCropping(null)} onDone={crop => { setPhoto({ bmp: cropping, crop }); setChosen('photo'); setChosenByUser(true); setCropping(null); }} />;
  if (scanning) return <IsbnScanner onCode={onIsbn} onClose={() => setScanning(false)} />;

  return (
    <Sheet open={open} onClose={close} title={count ? `Adaugă cărți (${count} salvate)` : 'Adaugă o carte'}
      footer={<>
        <label class="mr-auto flex items-center gap-2 text-xs text-ink-700"><input type="checkbox" checked={stack} class="accent-terra-500 h-4 w-4"
          onChange={e => { const v = (e.target as HTMLInputElement).checked; setStack(v); localStorage.setItem('cc10_stack', v ? '1' : '0'); }} /> Mod teanc</label>
        <button class="btn-ghost" onClick={close}>Închide</button>
        {existing
          ? <button class="btn-primary" onClick={() => save(true)}>Adaugă exemplar</button>
          : <button class="btn-primary" onClick={() => save(false)} data-testid="save-book">Salvează</button>}
      </>}>
      <div class="mb-4 grid grid-cols-2 gap-2">
        <button class="btn-primary !py-3" onClick={() => setScanning(true)} data-testid="scan-isbn">📷 Scanează ISBN</button>
        <button class="btn-ghost !py-3" onClick={() => fileRef.current?.click()}>📸 Poză copertă</button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" class="hidden"
          onChange={async e => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) setCropping(await blobToBitmap(f)); (e.target as HTMLInputElement).value = ''; }} />
      </div>
      {stack && <p class="mb-3 -mt-2 text-xs text-ink-500">Mod teanc: după „Salvează” camera se redeschide pentru cartea următoare; raftul și destinația rămân aceleași.</p>}
      {looking && <p class="mb-3 text-sm text-ink-500">Caut datele și coperta…</p>}

      {(cands.length > 0 || photo) && (
        <div class="mb-4">
          <span class="label">Coperta</span>
          <div class="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="add-cover-strip">
            {photo && (
              <button class={`shrink-0 w-16 rounded-lg overflow-hidden ${chosen === 'photo' ? 'ring-4 ring-terra-500' : ''}`} onClick={() => { setChosen('photo'); setChosenByUser(true); }}>
                <PhotoThumb ph={photo} />
              </button>
            )}
            {cands.slice(0, 8).map(cd => (
              <button key={cd.url} class={`shrink-0 w-16 aspect-[2/3] rounded-lg overflow-hidden bg-cream-200 ${chosen === cd.url ? 'ring-4 ring-terra-500' : ''}`}
                onClick={() => { setChosen(cd.url); setChosenByUser(true); }}>
                <img src={cd.thumb || cd.url} referrerpolicy="no-referrer" class="h-full w-full object-cover" />
              </button>
            ))}
            <button class={`shrink-0 w-16 aspect-[2/3] rounded-lg border-2 border-dashed border-cream-300 text-[10px] text-ink-500 ${chosen === null ? 'ring-4 ring-terra-500' : ''}`}
              onClick={() => { setChosen(null); setChosenByUser(true); }}>fără<br />(3D)</button>
          </div>
        </div>
      )}

      {existing && (
        <div class="mb-4 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="existing-hint">
          <Cover work={existing} size="sm" />
          <div class="text-sm">
            <div class="font-semibold">Cartea există deja în catalog</div>
            <div class="text-ink-700">„{existing.title}” — {existing.authors}. Salvarea adaugă <b>un exemplar nou</b> la aceeași fișă (fără dublură).</div>
          </div>
        </div>
      )}
      <WorkFields w={w} set={(k, v) => setW({ ...w, [k]: v })} />
      {!looking && cands.length === 0 && w.title && w.title.length > 2 && (
        <button class="mt-2 text-sm font-semibold text-terra-600" onClick={findCovers}>🔎 Caută coperta online</button>
      )}
      <div class="my-5 border-t border-cream-300" />
      <div class="mb-3 font-serif text-lg font-bold">Exemplarul meu</div>
      <CopyFields c={c} set={(k, v) => setC({ ...c, [k]: v })} shelves={shelves} />
    </Sheet>
  );
}

function PhotoThumb({ ph }: { ph: { bmp: ImageBitmap; crop?: Crop } }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const cr = ph.crop || fullCrop(ph.bmp);
    cv.width = 64; cv.height = 96;
    cv.getContext('2d')!.drawImage(ph.bmp, cr.x, cr.y, cr.w, cr.h, 0, 0, 64, 96);
  }, [ph]);
  return <canvas ref={ref} class="block w-16 aspect-[2/3]" />;
}

export function EditWorkSheet({ work, onClose }: { work: Work | null; onClose: () => void }) {
  const [w, setW] = useState<Partial<Work>>(work || {});
  if (!work) return null;
  const save = async () => {
    const changed: Partial<Work> = {};
    (Object.keys(w) as (keyof Work)[]).forEach(k => { if (w[k] !== work[k]) (changed as any)[k] = w[k]; });
    if (Object.keys(changed).length) { await updateWork(work.id, changed); toast('Modificări salvate'); }
    onClose();
  };
  return (
    <Sheet open onClose={onClose} title="Editează cartea" footer={<><button class="btn-ghost" onClick={onClose}>Renunță</button><button class="btn-primary" onClick={save}>Salvează</button></>}>
      <WorkFields w={w} set={(k, v) => setW({ ...w, [k]: v })} />
    </Sheet>
  );
}

export function EditCopySheet({ copy, onClose }: { copy: Copy | null; onClose: () => void }) {
  const shelves = useShelves();
  const [c, setC] = useState<Partial<Copy>>(copy || {});
  if (!copy) return null;
  const save = async () => {
    const changed: Partial<Copy> = {};
    (Object.keys(c) as (keyof Copy)[]).forEach(k => { if (c[k] !== copy[k]) (changed as any)[k] = c[k]; });
    if (Object.keys(changed).length) { await updateCopy(copy.id, changed); toast('Exemplar actualizat'); }
    onClose();
  };
  const remove = async () => {
    if (!confirm('Ștergi acest exemplar?')) return;
    await deleteCopies([copy.id]); toast('Exemplar șters'); onClose();
  };
  return (
    <Sheet open onClose={onClose} title="Exemplarul meu"
      footer={<><button class="btn-danger mr-auto" onClick={remove}>Șterge</button><button class="btn-ghost" onClick={onClose}>Renunță</button><button class="btn-primary" onClick={save}>Salvează</button></>}>
      <CopyFields c={c} set={(k, v) => setC({ ...c, [k]: v as any })} shelves={shelves} />
    </Sheet>
  );
}

export function AddCopySheet({ work, onClose }: { work: Work | null; onClose: () => void }) {
  const shelves = useShelves();
  const [c, setC] = useState<Partial<Copy>>({ destination: 'imprumut' as Destination, condition: 'Bună' });
  if (!work) return null;
  const save = async () => { await addBook({}, c, work.id); toast('Exemplar adăugat'); onClose(); };
  return (
    <Sheet open onClose={onClose} title="Am și eu această carte" footer={<><button class="btn-ghost" onClick={onClose}>Renunță</button><button class="btn-primary" onClick={save}>Adaugă exemplar</button></>}>
      <CopyFields c={c} set={(k, v) => setC({ ...c, [k]: v as any })} shelves={shelves} />
    </Sheet>
  );
}
